/**
 * A query opened from a spatial marker: the record, the text, the responses, and for users
 * who may act on queries, respond / close / reopen from the same card.
 */
import type * as THREE from 'three';
import { canModifyQueries, type Query, type QueryStatus, type Role } from '@clinspace/shared';
import type { CanvasPanel, Region } from '../dashboards/CanvasPanel.ts';
import {
  PALETTE,
  chip,
  font,
  text,
  truncate,
  wrapText,
  panelBackground,
} from '../dashboards/charts.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';
import { FloatingPanel, type FloatingPanelSounds } from './FloatingPanel.ts';
import { drawTextField } from './textField.ts';

export interface QueryCardHandlers {
  respond(query: Query, text: string): Promise<Query>;
  setStatus(query: Query, status: QueryStatus): Promise<Query>;
  /** Opens the dashboard drilled to this query's record. */
  showRecord?(query: Query): void;
}

type CardRegion = {
  kind: 'action';
  action: 'respond' | 'send' | 'cancel' | 'close' | 'reopen' | 'dismiss' | 'field' | 'record';
};

const STATUS_COLOUR: Record<QueryStatus, string> = {
  Open: PALETTE.amber,
  Answered: PALETTE.teal,
  Closed: PALETTE.forest,
  Cancelled: PALETTE.grey,
};

export class QueryCard extends FloatingPanel {
  query: Query | undefined;
  error: string | undefined;
  busy = false;

  constructor(
    interactive: InteractiveSystem,
    private readonly role: () => Role,
    private readonly handlers: QueryCardHandlers,
    sounds?: FloatingPanelSounds,
  ) {
    super(
      interactive,
      { width: 0.9, height: 0.64, pixelWidth: 630, pixelHeight: 448, name: 'query-card' },
      sounds,
    );
  }

  open(query: Query, origin: THREE.Vector3, viewer: THREE.Vector3): void {
    this.query = query;
    this.error = undefined;
    this.busy = false;
    this.field.clear();
    this.place(origin, viewer, 0.5);
    this.show();
    this.setTyping(false);
  }

  /** Live update from the room: redraw if this card shows that query. */
  refresh(query: Query): void {
    if (this.query?.id !== query.id) return;
    this.query = query;
    this.panel.invalidate();
  }

  get canAct(): boolean {
    return canModifyQueries(this.role());
  }

  async send(): Promise<boolean> {
    if (!this.query || this.busy) return false;
    const t = this.field.trimmed;
    if (!t) {
      this.error = 'Type a response first.';
      this.panel.invalidate();
      return false;
    }
    return this.run(async () => {
      this.query = await this.handlers.respond(this.query!, t);
      this.field.clear();
      this.setTyping(false);
    });
  }

  async setStatus(status: QueryStatus): Promise<boolean> {
    if (!this.query || this.busy) return false;
    return this.run(async () => {
      this.query = await this.handlers.setStatus(this.query!, status);
    });
  }

  private async run(fn: () => Promise<void>): Promise<boolean> {
    this.busy = true;
    this.error = undefined;
    this.panel.invalidate();
    try {
      await fn();
      this.sounds?.click();
      return true;
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
      return false;
    } finally {
      this.busy = false;
      this.panel.invalidate();
    }
  }

  state(): {
    open: boolean;
    id?: string;
    status?: QueryStatus;
    responses?: number;
    typing: boolean;
    error?: string;
  } {
    return {
      open: this.isOpen,
      id: this.query?.id,
      status: this.query?.status,
      responses: this.query?.responses.length,
      typing: this.typing,
      error: this.error,
    };
  }

  protected onEnter(): void {
    void this.send();
  }

  protected onEscape(): void {
    if (this.typing) this.setTyping(false);
    else this.hide();
  }

  protected draw(ctx: CanvasRenderingContext2D, panel: CanvasPanel): void {
    const W = panel.pxW;
    const H = panel.pxH;
    const q = this.query;
    panelBackground(ctx, W, H, 16);
    ctx.strokeStyle = PALETTE.tealDim;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(1.5, 1.5, W - 3, H - 3, 16);
    ctx.stroke();
    if (!q) return;
    const title = `Query · ${q.subjectId}`;
    text(ctx, title, 20, 32, { size: 17, weight: 700 });
    ctx.font = font(17, 700);
    const titleW = ctx.measureText(title).width;
    const status = chip(ctx, q.status, 20 + titleW + 14, 12, {
      active: true,
      h: 24,
      size: 11,
      colour: STATUS_COLOUR[q.status],
    });
    const pri = chip(ctx, `${q.priority} priority`, 20 + titleW + 14 + status.w + 8, 12, {
      h: 24,
      size: 11,
      colour: q.priority === 'High' ? PALETTE.crimson : PALETTE.grey,
    });
    void pri;
    const dismiss = chip(ctx, 'Dismiss', W - 92, 12, {
      hovered: panel.isHovered('dismiss'),
      h: 24,
      size: 11,
    });
    panel.region<CardRegion>({
      id: 'dismiss',
      ...dismiss,
      data: { kind: 'action', action: 'dismiss' },
    });

    const where = [q.study, q.visit, q.form, q.field].filter(Boolean).join(' · ');
    ctx.font = font(11);
    text(ctx, truncate(ctx, where, W - 160), 20, 56, { size: 11, colour: PALETTE.muted });
    if (q.currentValue)
      text(ctx, truncate(ctx, `Value: ${q.currentValue}`, W - 160), 20, 74, {
        size: 11,
        colour: PALETTE.muted,
      });
    if (this.handlers.showRecord) {
      const r = chip(ctx, 'Show record', W - 120, 46, {
        hovered: panel.isHovered('record'),
        h: 22,
        size: 10,
      });
      panel.region<CardRegion>({ id: 'record', ...r, data: { kind: 'action', action: 'record' } });
    }
    text(
      ctx,
      `${q.category} · raised ${q.raisedAt.slice(0, 10)} by ${q.raisedBy.name} (${q.raisedBy.role})`,
      20,
      92,
      { size: 10, colour: PALETTE.muted, maxWidth: W - 40 },
    );
    ctx.font = font(13);
    let y = 114;
    for (const line of wrapText(ctx, q.text, W - 40).slice(0, 4)) {
      text(ctx, line, 20, y, { size: 13 });
      y += 20;
    }
    y += 6;
    text(ctx, q.responses.length ? 'RESPONSES' : 'No responses yet', 20, y, {
      size: 10,
      colour: PALETTE.muted,
      weight: 600,
    });
    y += 8;
    const limit = this.typing ? 1 : 3;
    for (const r of q.responses.slice(-limit)) {
      ctx.font = font(12);
      const lines = wrapText(
        ctx,
        `${r.by} (${r.role}), ${r.at.slice(0, 10)}: ${r.text}`,
        W - 40,
      ).slice(0, 2);
      for (const l of lines) {
        y += 17;
        text(ctx, l, 20, y, { size: 12, colour: PALETTE.text });
      }
      y += 4;
    }
    if (q.closedAt) {
      y += 16;
      text(ctx, `${q.status} ${q.closedAt.slice(0, 10)} by ${q.closedBy?.name ?? ''}`, 20, y, {
        size: 11,
        colour: PALETTE.muted,
      });
    }

    // Actions.
    const bottom = H - 48;
    if (!this.canAct) {
      text(
        ctx,
        'Sponsor Observers can view queries but cannot respond or change status.',
        20,
        bottom + 20,
        { size: 11, colour: PALETTE.muted },
      );
      return;
    }
    const open = q.status === 'Open' || q.status === 'Answered';
    if (this.typing) {
      const fieldRect = { x: 20, y: bottom - 100, w: W - 40, h: 90 };
      drawTextField(ctx, fieldRect, this.field, {
        placeholder: 'Type your response',
        active: true,
        size: 13,
      });
      panel.region<CardRegion>({
        id: 'field',
        ...fieldRect,
        data: { kind: 'action', action: 'field' },
        selectable: false,
      });
      const send = chip(ctx, this.busy ? 'Sending…' : 'Send response', W - 150, bottom, {
        active: true,
        hovered: panel.isHovered('send'),
        h: 30,
        size: 13,
        colour: PALETTE.teal,
      });
      panel.region<CardRegion>({ id: 'send', ...send, data: { kind: 'action', action: 'send' } });
      const cancel = chip(ctx, 'Cancel', W - 240, bottom, {
        hovered: panel.isHovered('cancel'),
        h: 30,
        size: 13,
      });
      panel.region<CardRegion>({
        id: 'cancel',
        ...cancel,
        data: { kind: 'action', action: 'cancel' },
      });
    } else {
      let x = 20;
      if (open) {
        const respond = chip(ctx, 'Respond', x, bottom, {
          active: true,
          hovered: panel.isHovered('respond'),
          h: 30,
          size: 13,
          colour: PALETTE.teal,
        });
        panel.region<CardRegion>({
          id: 'respond',
          ...respond,
          data: { kind: 'action', action: 'respond' },
        });
        x += respond.w + 10;
        const close = chip(ctx, this.busy ? 'Working…' : 'Close query', x, bottom, {
          hovered: panel.isHovered('close'),
          h: 30,
          size: 13,
          colour: PALETTE.forest,
        });
        panel.region<CardRegion>({
          id: 'close',
          ...close,
          data: { kind: 'action', action: 'close' },
        });
      } else {
        const reopen = chip(ctx, this.busy ? 'Working…' : 'Reopen', x, bottom, {
          hovered: panel.isHovered('reopen'),
          h: 30,
          size: 13,
          colour: PALETTE.amber,
        });
        panel.region<CardRegion>({
          id: 'reopen',
          ...reopen,
          data: { kind: 'action', action: 'reopen' },
        });
      }
    }
    if (this.error)
      text(ctx, this.error, 20, H - 10, { size: 10, colour: PALETTE.crimson, maxWidth: W - 40 });
  }

  protected select(region: Region): void {
    const data = region.data as CardRegion | undefined;
    if (!data || !this.query) return;
    switch (data.action) {
      case 'dismiss':
        this.hide();
        break;
      case 'respond':
      case 'field':
        if (this.canAct) this.setTyping(true);
        break;
      case 'cancel':
        this.field.clear();
        this.setTyping(false);
        break;
      case 'send':
        void this.send();
        break;
      case 'close':
        void this.setStatus('Closed');
        break;
      case 'reopen':
        void this.setStatus('Open');
        break;
      case 'record':
        this.handlers.showRecord?.(this.query);
        break;
    }
  }
}
